import React, { useCallback, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { DMDiv } from '../../../components/Primitives';

// Mirrors the master-service sanitize_html allowlist: blog CONTENT arrives as
// clean semantic HTML. DOMPurify here is defense-in-depth for posts saved
// before server-side sanitization existed.
const ALLOWED_TAGS = [
  'h2', 'h3', 'p', 'ul', 'ol', 'li', 'a',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'strong', 'em', 'b', 'i', 'u', 'br',
];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

// The project has no Tailwind typography plugin, so styled the blog elements
// explicitly — otherwise preflight leaves headings/lists/tables looking like
// plain text.
const CONTENT_STYLES = [
  '[&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-900',
  '[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-900',
  '[&_p]:mb-4 [&_p]:leading-relaxed',
  '[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6',
  '[&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6',
  '[&_li]:mb-1',
  '[&_a]:text-[#5E4DE0] [&_a]:underline [&_a:hover]:text-[#4638C0]',
  '[&_table]:mb-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm',
  '[&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top',
].join(' ');

interface BlogHtmlContentProps {
  html: string;
  className?: string;
}

/**
 * Renders stored blog HTML (headings, lists, tables, links) safely.
 *
 * Link behavior: external links open in a new tab; relative hrefs are
 * internal links between blog posts that only resolve on the published
 * school website, so they are inert in the portal preview.
 */
export const BlogHtmlContent: React.FC<BlogHtmlContentProps> = ({
  html,
  className = '',
}) => {
  const sanitizedHtml = useMemo(
    () =>
      DOMPurify.sanitize(html || '', {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
      }),
    [html],
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a');
    if (!anchor) {
      return;
    }
    e.preventDefault();
    const href = anchor.getAttribute('href') || '';
    if (/^https?:\/\//i.test(href)) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return (
    <DMDiv
      className={`max-w-none text-gray-700 leading-relaxed ${CONTENT_STYLES} ${className}`}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};
